// src/hooks/useProfile.js - WITH ALL NOTIFICATION SOURCES
import { useState, useEffect } from 'react';
import { profileService } from '../services/profileService';
import { watchlistService } from '../services/watchlistService';

export const useProfile = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videos, setVideos] = useState([]);
  const [news, setNews] = useState([]);
  const [newsCategories, setNewsCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  // ✅ Listen for custom alert refresh events
  useEffect(() => {
    const handleRefreshAlerts = () => {
      console.log('📢 Alert refresh event received, fetching alerts...');
      fetchAlerts();
    };

    window.addEventListener('refreshAlerts', handleRefreshAlerts);
    return () => window.removeEventListener('refreshAlerts', handleRefreshAlerts);
  }, []);

  // Fetch user profile
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getUserProfile();
      setUserProfile(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch profile');
      console.error('Error fetching profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update profile
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.updateProfile(profileData);
      setUserProfile(data.profile);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      console.error('Error updating profile:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getBankAccounts();
      setBankAccounts(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch bank accounts');
      console.error('Error fetching bank accounts:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const addBankAccount = async (bankData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.addBankAccount(bankData);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to add bank account');
      console.error('Error adding bank account:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateBankAccount = async (accountId, bankData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.updateBankAccount(accountId, bankData);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update bank account');
      console.error('Error updating bank account:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setDefaultBankAccount = async (accountId) => {
    try {
      setLoading(true);
      setError(null);
      await profileService.setDefaultBankAccount(accountId);
      await fetchBankAccounts();
    } catch (err) {
      setError(err.message || 'Failed to set default account');
      console.error('Error setting default account:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteBankAccount = async (accountId) => {
    try {
      setLoading(true);
      setError(null);
      await profileService.deleteBankAccount(accountId);
      await fetchBankAccounts();
    } catch (err) {
      setError(err.message || 'Failed to delete bank account');
      console.error('Error deleting bank account:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getVideos();
      setVideos(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch videos');
      console.error('Error fetching videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVideoDetail = async (videoId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getVideoDetail(videoId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch video details');
      console.error('Error fetching video details:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchNews = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getNews(params);
      setNews(data.news || data);
    } catch (err) {
      setError(err.message || 'Failed to fetch news');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsDetail = async (slug) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.getNewsDetail(slug);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch news details');
      console.error('Error fetching news details:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsCategories = async () => {
    try {
      setError(null);
      const data = await profileService.getNewsCategories();
      setNewsCategories(data);
    } catch (err) {
      setError(err.message || 'Failed to fetch categories');
      console.error('Error fetching categories:', err);
    }
  };

  // Fetch alerts — merges all 5 notification sources, sorted by date
  const fetchAlerts = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      // All five sources fetched in parallel — adding one more to Promise.all
      // costs nothing extra since they all run simultaneously
      const [
        investmentPlanData,
        walletData,
        depositData,
        withdrawalData,
        priceAlertData,
      ] = await Promise.all([
        profileService.getAlerts(params),              // investment + plan alerts
        profileService.getWalletNotifications(),       // wallet credits/debits
        profileService.getDepositNotifications(),      // deposit rejections
        profileService.getWithdrawalNotifications(),   // withdrawal rejections
        watchlistService.getPriceAlerts(),             // watchlist price change alerts (NEW)
      ]);

      const merged = [
        ...investmentPlanData,
        ...walletData,
        ...depositData,
        ...withdrawalData,
        ...priceAlertData,
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      console.log(
        `✅ Alerts fetched: ${investmentPlanData.length} investment/plan + ` +
        `${walletData.length} wallet + ${depositData.length} deposit + ` +
        `${withdrawalData.length} withdrawal + ` +
        `${priceAlertData.length} price alerts = ${merged.length} total`
      );

      setAlerts(merged);
    } catch (err) {
      setError(err.message || 'Failed to fetch alerts');
      console.error('Error fetching alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  // Mark a single notification as read — handles all five sources
  const markNotificationRead = async (notificationId, source) => {
    try {
      setError(null);

      if (source === 'investment') {
        await profileService.markInvestmentNotificationRead(notificationId);
      } else if (source === 'plan') {
        await profileService.markPlanNotificationRead(notificationId);
      } else if (source === 'wallet') {
        await profileService.markWalletNotificationRead(notificationId);
      } else if (source === 'deposit') {
        await profileService.markDepositNotificationRead(notificationId);
      } else if (source === 'withdrawal') {
        await profileService.markWithdrawalNotificationRead(notificationId);
      }

      await fetchAlerts();
    } catch (err) {
      setError(err.message || 'Failed to mark notification as read');
      console.error('Error marking notification as read:', err);
      throw err;
    }
  };

  // Mark all notifications as read across all sources
  const markAllNotificationsRead = async () => {
    try {
      setError(null);
      await profileService.markAllNotificationsRead();
      await fetchAlerts();
    } catch (err) {
      setError(err.message || 'Failed to mark all notifications as read');
      console.error('Error marking all notifications as read:', err);
      throw err;
    }
  };

  const updateUsername = async (username) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.updateUsername(username);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update username');
      console.error('Error updating username:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setLoading(true);
      setError(null);
      const data = await profileService.changePassword(passwordData);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to change password');
      console.error('Error changing password:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    // State
    loading,
    error,
    videos,
    news,
    newsCategories,
    alerts,
    userProfile,
    bankAccounts,

    // Methods
    fetchVideos,
    fetchVideoDetail,
    fetchNews,
    fetchNewsDetail,
    fetchNewsCategories,
    fetchAlerts,
    fetchUserProfile,
    updateProfile,
    changePassword,
    fetchBankAccounts,
    addBankAccount,
    updateBankAccount,
    setDefaultBankAccount,
    deleteBankAccount,
    markNotificationRead,
    markAllNotificationsRead,
  };
};